/**
 * Rough.js utility wrapper for hand-drawn markup rendering
 * Feature: Hand-drawn line style for critique photo markup
 *
 * Provides helper functions to render shapes with a sketchy, hand-drawn appearance
 * using the Rough.js library when the strokeDasharray is '__hand_drawn__'.
 */

import rough from 'roughjs';
import type { RoughSVG } from 'roughjs/bin/svg';
import type { Options } from 'roughjs/bin/core';

/**
 * Check if a shape should use hand-drawn rendering
 */
export function isHandDrawn(strokeDasharray: string): boolean {
  return strokeDasharray === '__hand_drawn__';
}

/**
 * Get Rough.js options from shape style properties
 */
function getRoughOptions(
  strokeColor: string,
  strokeWidth: number,
  isSelected: boolean
): Options {
  return {
    stroke: strokeColor,
    strokeWidth: isSelected ? strokeWidth * 1.5 : strokeWidth,
    roughness: 1.0, // Natural hand-drawn feel (0.5-2.5 range)
    bowing: 1.0, // Natural curve intensity
    fill: 'none',
    fillStyle: 'solid',
  };
}

/**
 * Create a Rough.js SVG renderer instance
 * Note: This should be called once per SVG element
 */
export function createRoughSVG(svgElement: SVGSVGElement): RoughSVG {
  return rough.svg(svgElement);
}

/**
 * Render a hand-drawn rectangle
 * Returns an SVG group element containing the rough.js drawable
 */
export function renderHandDrawnRectangle(
  rc: RoughSVG,
  x: number,
  y: number,
  width: number,
  height: number,
  strokeColor: string,
  strokeWidth: number,
  isSelected: boolean
): SVGGElement {
  const options = getRoughOptions(strokeColor, strokeWidth, isSelected);
  const drawable = rc.rectangle(x, y, width, height, options);
  return drawable as SVGGElement;
}

/**
 * Render a hand-drawn circle
 * Returns an SVG group element containing the rough.js drawable
 */
export function renderHandDrawnCircle(
  rc: RoughSVG,
  cx: number,
  cy: number,
  diameter: number,
  strokeColor: string,
  strokeWidth: number,
  isSelected: boolean
): SVGGElement {
  const options = getRoughOptions(strokeColor, strokeWidth, isSelected);
  const drawable = rc.circle(cx, cy, diameter, options);
  return drawable as SVGGElement;
}

/**
 * Render a hand-drawn line (for arrows)
 * Returns an SVG group element containing the rough.js drawable
 */
export function renderHandDrawnLine(
  rc: RoughSVG,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  strokeColor: string,
  strokeWidth: number,
  isSelected: boolean
): SVGGElement {
  const options = getRoughOptions(strokeColor, strokeWidth, isSelected);
  const drawable = rc.line(x1, y1, x2, y2, options);
  return drawable as SVGGElement;
}

/**
 * Render a hand-drawn polygon (for arrow heads)
 * Returns an SVG group element containing the rough.js drawable
 */
export function renderHandDrawnPolygon(
  rc: RoughSVG,
  points: Array<[number, number]>,
  strokeColor: string,
  strokeWidth: number,
  isSelected: boolean
): SVGGElement {
  const options = {
    ...getRoughOptions(strokeColor, strokeWidth, isSelected),
    fill: strokeColor, // Arrow heads should be filled
    fillStyle: 'solid' as const,
  };
  const drawable = rc.polygon(points, options);
  return drawable as SVGGElement;
}
